import FAQItems from "../components/FAQItems";

const TEXT = "#EDEDED";
const TEXT_DIM = "#a0a0a0";
const TEAL = "#4FCDC4";
const TEAL_DIM = "rgba(79,205,196,0.15)";
const CARD_BORDER = "rgba(255,255,255,0.04)";

function H2({ id, children }) {
  return <h2 id={id} style={{ fontFamily: "var(--display)", fontWeight: 700, fontSize: "clamp(22px, 3vw, 28px)", color: TEXT, margin: "48px 0 16px", letterSpacing: "-0.01em", lineHeight: 1.2 }}>{children}</h2>;
}
function H3({ children }) {
  return <h3 style={{ fontFamily: "var(--display)", fontWeight: 700, fontSize: 19, color: TEXT, margin: "32px 0 12px", letterSpacing: "-0.005em", lineHeight: 1.3 }}>{children}</h3>;
}
function P({ children }) {
  return <p style={{ fontFamily: "var(--body)", fontSize: 16, color: TEXT_DIM, lineHeight: 1.85, margin: "0 0 18px" }}>{children}</p>;
}
function Li({ children }) {
  return <li style={{ fontFamily: "var(--body)", fontSize: 16, color: TEXT_DIM, lineHeight: 1.8, marginBottom: 8 }}>{children}</li>;
}
function Strong({ children }) {
  return <strong style={{ color: TEXT, fontWeight: 700 }}>{children}</strong>;
}
function A({ href, children, external }) {
  const isExternal = external || href.startsWith("http");
  return <a href={href} {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})} style={{ color: TEAL, textDecoration: "underline", fontWeight: 600 }}>{children}</a>;
}
function Code({ children }) {
  return <code style={{ fontFamily: "var(--mono)", fontSize: 14, color: TEXT, background: "rgba(255,255,255,0.06)", padding: "2px 6px", borderRadius: 4 }}>{children}</code>;
}
function PullQuote({ children }) {
  return (
    <blockquote style={{ margin: "32px 0", padding: "20px 24px", borderLeft: `3px solid ${TEAL}`, fontFamily: "var(--display)", fontSize: 20, fontWeight: 600, color: TEXT, lineHeight: 1.4, fontStyle: "normal" }}>
      {children}
    </blockquote>
  );
}

/* ── Visual 1: the three surfaces where the preferred badge appears ── */
function SurfacesCard() {
  const surfaces = [
    { name: "Top Stories", desc: "The original surface. Your articles appear more often, with a preferred badge, for users who chose you." },
    { name: "AI Overviews", desc: "Google's AI-generated answer box. A preferred source can carry the badge inside the overview." },
    { name: "AI Mode", desc: "Google's conversational search experience. Preferred sources surface with the badge in the response." },
  ];
  return (
    <div style={{ background: "#0f0f11", borderRadius: 12, border: `1px solid ${CARD_BORDER}`, padding: 24, margin: "16px 0 32px" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: TEXT, marginBottom: 4, fontFamily: "var(--body)" }}>Where the &ldquo;preferred&rdquo; badge appears</div>
      <div style={{ fontSize: 10, color: TEXT_DIM, marginBottom: 20, fontFamily: "var(--body)" }}>For users who selected your site, across three Google surfaces.</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12 }}>
        {surfaces.map((s) => (
          <div key={s.name} style={{ padding: 14, borderRadius: 10, background: "rgba(255,255,255,0.03)", border: `1px solid ${CARD_BORDER}` }}>
            <div style={{ display: "inline-block", padding: "2px 8px", borderRadius: 6, background: TEAL_DIM, color: TEAL, fontFamily: "var(--mono)", fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", marginBottom: 10 }}>preferred</div>
            <div style={{ fontFamily: "var(--display)", fontSize: 15, fontWeight: 700, color: TEXT, marginBottom: 6 }}>{s.name}</div>
            <div style={{ fontSize: 12, color: TEXT_DIM, lineHeight: 1.5 }}>{s.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Visual 2: eligibility (domain/subdomain vs subdirectory) ── */
function EligibilityCard() {
  const rows = [
    { url: "https://www.example.com/", ok: true, note: "Domain level - eligible" },
    { url: "https://code.example.com/", ok: true, note: "Subdomain level - eligible" },
    { url: "https://www.example.com/blog", ok: false, note: "Subdirectory - not eligible" },
  ];
  return (
    <div style={{ background: "#0f0f11", borderRadius: 12, border: `1px solid ${CARD_BORDER}`, padding: 24, margin: "16px 0 32px" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: TEXT, marginBottom: 4, fontFamily: "var(--body)" }}>What is eligible to be a preferred source</div>
      <div style={{ fontSize: 10, color: TEXT_DIM, marginBottom: 16, fontFamily: "var(--body)" }}>Only domain and subdomain level. Subdirectories cannot be selected.</div>
      {rows.map((r) => (
        <div key={r.url} style={{ display: "grid", gridTemplateColumns: "20px 1fr", gap: 10, alignItems: "start", padding: "10px 0", borderTop: `1px solid ${CARD_BORDER}` }}>
          <span style={{ color: r.ok ? TEAL : "#f87171", fontSize: 16, fontWeight: 700, lineHeight: 1.4 }}>{r.ok ? "✓" : "✗"}</span>
          <div>
            <code style={{ fontFamily: "var(--mono)", fontSize: 13, color: TEXT, wordBreak: "break-all" }}>{r.url}</code>
            <div style={{ fontSize: 12, color: TEXT_DIM, marginTop: 2 }}>{r.note}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

const faqs = [
  {
    q: "What is Google Preferred Sources in simple terms?",
    a: "Google Preferred Sources is a Search personalization feature that lets a user star the websites they trust most. Once a user selects your site as a preferred source, your content is more likely to appear for that user, highlighted with a “preferred” badge, in Top Stories, AI Overviews, and AI Mode. It is opt-in by the reader, not bought by the advertiser, and it works at the level of the individual user who chose you.",
  },
  {
    q: "Which Google surfaces does Preferred Sources affect?",
    a: "Three: Top Stories (the original surface), AI Overviews (Google's AI-generated answer box), and AI Mode (Google's conversational search). In each, content from a site the user selected can carry a “preferred” badge and surface more prominently. Preferred Sources started in Top Stories at launch in August 2025 and expanded into AI Overviews and AI Mode in 2026, which is what makes it relevant to AI search visibility, not just news.",
  },
  {
    q: "Is Preferred Sources only for news publishers?",
    a: "No. The original August 2025 launch was framed around news and Top Stories, but Google's Search Central documentation places no news restriction on eligibility. Any site that meets the domain or subdomain eligibility rule can appear in the source preferences tool. For B2B brands that publish a blog, a resource hub, or a newsroom, the feature is available the same way it is for a news outlet.",
  },
  {
    q: "How do users add my site as a preferred source?",
    a: "A user can search a topic, open the source-preferences tool, and select your site. As a publisher you can make that easier: Google provides a deeplink in the format https://google.com/preferences/source?q=your-url and downloadable button assets reading “Add as a preferred source on Google” in 15 languages. You add the link or button to your newsletter, social posts, or site. Prompting your audience is optional, not required, to be eligible.",
  },
  {
    q: "Is my whole site eligible, or only certain pages?",
    a: "Only domain-level and subdomain-level URLs are eligible to appear in the source preferences tool. For example, https://www.example.com/ and https://code.example.com/ are eligible, but the subdirectory https://www.example.com/blog is not. Users select an entire domain or subdomain, not an individual section or article, so the unit of preference is the property, not the page.",
  },
  {
    q: "Does becoming a preferred source guarantee I will appear in AI Overviews?",
    a: "No. Preferred Sources increases the likelihood that your content surfaces, with a badge, for the specific users who selected you. It is a personalization signal, not a ranking override. Your content still has to be relevant and eligible for the query, and the effect is scoped to users who chose you, not to all searchers. It complements organic answer engine optimization rather than replacing it.",
  },
  {
    q: "How is this different from getting cited by AI Overviews organically?",
    a: "Organic AI citation is earned at the system level: Google's models decide your page is a good source for a query, for everyone. Preferred Sources is chosen at the user level: an individual reader tells Google they want more of you. The two compound. Earning broad organic citations builds the baseline; preferred-source selections layer a personalized boost on top for your most engaged audience. The strongest AI visibility strategies pursue both.",
  },
];

function Content() {
  return (
    <article style={{ maxWidth: 720, margin: "0 auto" }}>
      {/* Overview box */}
      <div style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${CARD_BORDER}`, borderRadius: 12, padding: "20px 24px", marginBottom: 32 }} id="quick-answer">
        <P>
          <Strong>Google Preferred Sources is a Search personalization feature that lets users star the sites they trust, so that content appears more often for them, with a &ldquo;preferred&rdquo; badge, in Top Stories, <A href="/blog/how-to-get-cited-by-google-ai-overviews">AI Overviews</A>, and AI Mode.</Strong> It is the first time readers can directly tell Google which sources to favor in its AI answer surfaces, which makes it a new, user-driven lever for AI visibility, alongside the organic citation work that earns your way into those answers in the first place.
        </P>
        <ul style={{ margin: "12px 0 0", paddingLeft: 20 }}>
          <Li><Strong>What it is:</Strong> a reader opt-in. Users select your site; you do not buy the placement.</Li>
          <Li><Strong>Where the badge shows:</Strong> Top Stories, AI Overviews, and AI Mode.</Li>
          <Li><Strong>Who is eligible:</Strong> domain and subdomain level only. Subdirectories like <Code>example.com/blog</Code> are not eligible.</Li>
          <Li><Strong>Not news-only:</Strong> any eligible site, including B2B blogs and resource hubs.</Li>
          <Li><Strong>How to promote it:</Strong> a Google-provided deeplink and an &ldquo;Add as a preferred source&rdquo; button (optional, not required to be eligible).</Li>
        </ul>
      </div>

      {/* §1 */}
      <H2 id="what-is-google-preferred-sources">What is Google Preferred Sources?</H2>
      <P>
        Google Preferred Sources is a personalization feature in Google Search. Per <A href="https://developers.google.com/search/docs/appearance/preferred-sources" external>Google&#39;s Search Central documentation</A>, when a user selects your site as a preferred source, your content is more likely to appear for that user, highlighted with a &ldquo;preferred&rdquo; badge. The reader is in control: they choose the sources they want to see more of, and Google weights those selections into what it shows them.
      </P>
      <P>
        That single mechanic is what makes the feature unusual. Almost every other lever in search and AI visibility is earned or bought at the system level, where you optimize a page or buy a placement and the algorithm decides whether to show it to everyone. Preferred Sources is decided at the user level. An individual reader raises their hand and says they trust you, and Google honors that preference in the surfaces below.
      </P>
      <P>
        Google launched Preferred Sources in <Strong>August 2025</Strong>, initially in the United States and India and scoped to Top Stories. In <Strong>2026</Strong> it expanded into AI Overviews and AI Mode, which is the change that moved it from a news-personalization feature into an <A href="/glossary/answer-engine-optimization">answer engine optimization</A> consideration. If your buyers are using Google&#39;s AI surfaces to research, a preferred-source selection now influences whether your brand shows up in those answers for them.
      </P>
      <PullQuote>
        Almost every visibility lever is decided by the algorithm. A preferred source is decided by the reader.
      </PullQuote>

      {/* §2 */}
      <H2 id="where-the-badge-appears">Where the preferred badge appears</H2>
      <P>
        A preferred-source selection follows the user across three Google surfaces. In each one, content from a site the user chose can carry the &ldquo;preferred&rdquo; badge and appear more prominently.
      </P>

      <SurfacesCard />

      <P>
        The Top Stories effect is the oldest and most direct: a selected source shows up more frequently, badged, in the carousel. The <A href="/blog/how-to-get-cited-by-google-ai-overviews">AI Overviews</A> and <A href="/blog/google-ai-mode-vs-ai-overviews">AI Mode</A> effects are the newer and more strategically important ones, because they sit inside the answers buyers increasingly read instead of clicking. A badge inside an AI Overview is a trust signal rendered at the exact moment a prospect is evaluating sources.
      </P>

      {/* §3 */}
      <H2 id="who-is-eligible">Who is eligible to be a preferred source?</H2>
      <P>
        Eligibility is a technical rule, and it is precise. Per Google&#39;s documentation, <Strong>only domain-level and subdomain-level sites are eligible</Strong> to appear in the source preferences tool. A subdirectory is not eligible.
      </P>

      <EligibilityCard />

      <P>
        The practical implication: if your content lives on a subdirectory of a larger domain you do not control, such as a profile on a third-party platform, users cannot select it as a preferred source. The property they select is the whole domain or subdomain. For most B2B brands publishing on their own domain or a blog subdomain, this is not a constraint. For brands that have outsourced their content to a subdirectory of someone else&#39;s site, it is a reason to own the property.
      </P>
      <P>
        There is no news-only restriction in the eligibility rule. The August 2025 announcement was framed around news and Top Stories, which led to a common assumption that the feature is for publishers. The Search Central documentation does not carry that limit. Any site meeting the domain or subdomain rule can be selected, which puts B2B blogs, resource centers, and company newsrooms on the same footing as a news outlet.
      </P>

      {/* §4 */}
      <H2 id="how-users-choose">How users choose a preferred source</H2>
      <P>
        From the reader&#39;s side, choosing a preferred source takes a few taps inside Search. From the publisher&#39;s side, you cannot add yourself, but you can make it easy for your audience to add you. Google provides two assets for that, and using them is explicitly optional, not a requirement to be eligible.
      </P>
      <H3>The deeplink</H3>
      <P>
        Google supplies a deeplink that takes a user straight to the screen where they can add your site. The format is <Code>https://google.com/preferences/source?q=your-url</Code>, for example <Code>https://google.com/preferences/source?q=example.com</Code>. You can drop that link into a newsletter, a social post, or a call to action on your site, anywhere you already ask your audience to follow or subscribe.
      </P>
      <H3>The button</H3>
      <P>
        Google also provides downloadable button assets that read &ldquo;Add as a preferred source on Google,&rdquo; available in 15 languages plus an all-languages bundle. The button is the same idea as a follow or subscribe button: a recognizable, low-friction prompt you place alongside your other audience calls to action. Neither the link nor the button is required to appear as a preferred source; they simply lower the friction for the audience you already have.
      </P>

      {/* §5 */}
      <H2 id="why-it-matters">Why Preferred Sources matters for AI visibility</H2>
      <P>
        <Strong>Preferred Sources is the first lever that lets your audience vote you into Google&#39;s AI answers.</Strong> Until now, getting cited inside an AI Overview was something you earned indirectly, by being a strong enough source that Google&#39;s models chose you for everyone. Preferred Sources adds a second, parallel path: the people who already trust you can tell Google to favor you in their AI answers directly.
      </P>
      <P>
        For B2B marketers, that maps cleanly onto an audience you already cultivate. Your newsletter subscribers, your customers, your community, and your event attendees are exactly the people most likely to add you as a preferred source. Each one who does is a person who will see your brand badged in their AI Overviews and AI Mode answers the next time they research your category. It is a way to convert owned-audience trust into AI search visibility.
      </P>
      <P>
        It does not replace <A href="/blog/how-to-get-cited-by-google-ai-overviews">earning organic citations in Google AI Overviews</A>; it compounds with it. Organic citation builds the baseline that reaches everyone. Preferred-source selections layer a personalized boost on top for your most engaged readers. The brands that win AI visibility in 2026 will run both: structure their content to be cited at the system level, and prompt their audience to be selected at the user level.
      </P>
      <PullQuote>
        Earn citations at the system level. Win preferred-source selections at the user level. The two compound.
      </PullQuote>

      {/* §6 */}
      <H2 id="what-it-is-not">What Google Preferred Sources is NOT</H2>
      <P>
        Four clarifications, because the feature is easy to misread.
      </P>
      <H3>It is not a paid placement</H3>
      <P>
        There is no auction and no spend. You cannot buy your way into being a preferred source. The only way in is a reader choosing you, which makes the lever fundamentally an audience-trust lever, not a media-buying one.
      </P>
      <H3>It is not a ranking factor you control</H3>
      <P>
        Being selected raises the likelihood that your content surfaces for the users who chose you. It does not rewrite your ranking for everyone, and it does not guarantee a placement on any given query. It is a personalization weight, scoped to the people who opted in.
      </P>
      <H3>It is not news-only</H3>
      <P>
        The framing at launch was news, but the eligibility rule is not. Any domain or subdomain qualifies, which is why it belongs in a B2B AI visibility strategy and not just a publisher&#39;s playbook.
      </P>
      <H3>It is not the same as being cited</H3>
      <P>
        A preferred-source badge and an organic AI citation are different things. One is a personalized trust marker for users who picked you; the other is a system-level decision that your page is a good answer source. You want both, and the difference is the subject of our guide on the broader <A href="/blog/google-ai-mode-vs-ai-overviews">Google AI Mode versus AI Overviews</A> distinction.
      </P>

      {/* FAQPage JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map((faq) => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": { "@type": "Answer", "text": faq.a },
        })),
      }) }} />

      {/* FAQ */}
      <H2 id="faq">Frequently Asked Questions</H2>
      <FAQItems faqs={faqs} />

      {/* Related Reading */}
      <H2 id="related">Related Reading</H2>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><A href="/blog/how-to-become-a-google-preferred-source">How to Become a Google Preferred Source: A B2B Playbook</A></Li>
        <Li><A href="/blog/why-google-preferred-sources-matter">Why Google Preferred Sources Is Your Newest AI-Visibility Lever</A></Li>
        <Li><A href="/blog/how-to-get-cited-by-google-ai-overviews">How to Get Cited by Google AI Overviews</A></Li>
        <Li><A href="/blog/google-ai-mode-vs-ai-overviews">Google AI Mode vs Google AI Overviews: What&#39;s the Difference?</A></Li>
        <Li><A href="/blog/google-ai-mode-ad-formats">Google AI Mode Ads: The New Formats and What B2B Marketers Should Do</A></Li>
        <Li><A href="/blog/what-is-generative-engine-optimization">What Is Generative Engine Optimization (GEO)? The Complete Guide</A></Li>
        <Li><A href="/blog/how-to-optimize-website-for-ai-search">How to Optimize a Website for AI Search (2026)</A></Li>
      </ul>
    </article>
  );
}

const post = {
  slug: "what-is-google-preferred-sources",
  title: "What Is Google Preferred Sources? The New AI Visibility Lever",
  seoTitle: "What Is Google Preferred Sources? (2026)",
  excerpt: "Google Preferred Sources lets users star the sites they trust, giving those sites a preferred badge and boosted placement in Top Stories, AI Overviews, and AI Mode. Here is what it is, who is eligible, and why it is a new lever for AI visibility.",
  metaDescription: "Google Preferred Sources lets users star sites for a preferred badge in Top Stories, AI Overviews, and AI Mode. What it is, who's eligible, and why it matters.",
  date: "2026-06-03",
  readTime: "9 min read",
  category: "Answer Engine Optimization",
  tags: ["Google Preferred Sources", "AI visibility", "Google AI Overviews", "AI Mode", "answer engine optimization", "Top Stories"],
  Content,
};

export default post;
